import React from 'react'
import PropTypes from 'prop-types'
import { Text, Price, Level, Clipped, Divider, Button, Sticky, Box, Disclosure } from '@deseretbook/react-ui'
import { withCheckout } from './CheckoutContext'
import PlatinumPointsDisplay from '../PlatinumPointsValue'
import CheckoutSidebarContent from './CheckoutSidebarContent'
import CheckoutAddons from './CheckoutAddons'

/**
 * Checkout order summary page content.
 *
 * @returns {function} Component
 */
const OrderSummary = ({
  step,
  order,
  submitOrder,
  isLoading,
  isSubmitting,
}) => {
  const appliedGiftCardBalance = order.payments.filter(({ state }) => state === 'checkout')
    .filter(({ sourceType }) => sourceType === 'DeseretBook::GiftCard')
    .reduce((total, giftCard) => total + parseFloat(giftCard.amount), 0)

  let orderTotal = order.displayTotal

  if (appliedGiftCardBalance > 0) {
    orderTotal = `$${order.total - appliedGiftCardBalance}`
  }

  return (
    <Box isColumn justifyContent="space-between" isFullheight isFullwidth>
      <Sticky background="alternate">
        <Box paddingVertical={1}>
          <Box paddingBottom={0.5}>
            <Level>
              <Level.Left isShrinkable>
                <Level.Item>
                  <Text hasEllipsis>Subtotal</Text>
                </Level.Item>
              </Level.Left>
              <Level.Right>
                <Level.Item>
                  <Text hasEllipsis><Price value={order.displayItemTotal} /></Text>
                </Level.Item>
              </Level.Right>
            </Level>
          </Box>
          <Box paddingBottom={0.5}>
            <Level>
              <Level.Left isShrinkable>
                <Level.Item>
                  <Clipped>
                    <Text hasEllipsis>Shipping</Text>
                    {
                      order.shipments.map(({
                        shippingRates,
                        shippingMethods,
                        id: shipmentId,
                        retailStore,
                      }) => {
                        const selectedShippingRate = shippingRates
                          .filter(({ selected }) => selected)[0]
                        const selectedShippingMethod = selectedShippingRate && shippingMethods
                          .filter(({ id }) => selectedShippingRate.shippingMethodId === id)[0]

                        if (!selectedShippingMethod) {
                          return null
                        }

                        const isStorePickup = retailStore && selectedShippingMethod.adminName === 'Store Pickup'

                        return (
                          <Text size="tiny" key={shipmentId}>
                            {
                              isStorePickup && retailStore && retailStore.displayName
                                ? `${selectedShippingMethod.name} (${retailStore.displayName} ${retailStore.shippingTime})`
                                : selectedShippingMethod.name
                            }
                          </Text>
                        )
                      })
                    }
                  </Clipped>
                </Level.Item>
              </Level.Left>
              <Level.Right>
                <Level.Item>
                  <Text hasEllipsis><Price value={order.displayShipTotal} /></Text>
                </Level.Item>
              </Level.Right>
            </Level>
          </Box>

          {
            order.platinumPointsTotal < 0
              ? (
                <Box paddingBottom={0.5}>
                  <Level>
                    <Level.Left isShrinkable>
                      <Level.Item>
                        <Text hasEllipsis>Applied Platinum Points</Text>
                      </Level.Item>
                    </Level.Left>
                    <Level.Right>
                      <Level.Item>
                        <Text hasEllipsis>
                          <PlatinumPointsDisplay points={parseFloat(order.platinumPointsTotal)} />
                        </Text>
                      </Level.Item>
                    </Level.Right>
                  </Level>
                </Box>
              )
              : null
          }
          {
            order.allAdjustments
              .filter(({ eligible, sourceType }) => eligible && (sourceType === 'Spree::PromotionAction' || sourceType === 'Spree::StockLocation'))
              .map(({
                id,
                label,
                amount,
                promotionCode,
              }) => (
                <Box paddingBottom={0.5} key={id}>
                  <Level>
                    <Level.Left isShrinkable>
                      <Level.Item>
                        <Text hasEllipsis>
                          {(promotionCode && promotionCode.value)
                            ? `Promotion (${promotionCode.value})`
                            : label}
                        </Text>
                      </Level.Item>
                    </Level.Left>
                    <Level.Right>
                      <Level.Item>
                        <Text hasEllipsis><Price value={amount} /></Text>
                      </Level.Item>
                    </Level.Right>
                  </Level>
                  {promotionCode && promotionCode.promotion && (
                    <Disclosure>
                      <Disclosure.Header>See details</Disclosure.Header>
                      <Disclosure.Content>
                        <Text hasEllipsis size="small" isBold>
                          {promotionCode.promotion.name}
                        </Text>
                        <Text display="block" size="tiny">{promotionCode.promotion.description}</Text>
                      </Disclosure.Content>
                    </Disclosure>
                  )}
                </Box>
              ))
          }
          <Box paddingBottom={0.5}>
            <Level>
              <Level.Left isShrinkable>
                <Level.Item>
                  <Text hasEllipsis>
                    {
                      order.state === 'confirm'
                        ? 'Taxes & Fees'
                        : 'Estimated Taxes & Fees'
                    }
                  </Text>
                </Level.Item>
              </Level.Left>
              <Level.Right>
                <Level.Item>
                  <Text hasEllipsis><Price value={order.displayTaxTotal} /></Text>
                </Level.Item>
              </Level.Right>
            </Level>
          </Box>
          {
            appliedGiftCardBalance > 0
              ? (
                <Box paddingBottom={0.5}>
                  <Level>
                    <Level.Left isShrinkable>
                      <Level.Item>
                        <Text hasEllipsis>Applied Gift Cards</Text>
                      </Level.Item>
                    </Level.Left>
                    <Level.Right>
                      <Level.Item>
                        <Text hasEllipsis> <Price value={-appliedGiftCardBalance} /></Text>
                      </Level.Item>
                    </Level.Right>
                  </Level>
                </Box>
              )
              : null
          }

          <Divider />

          <Level>
            <Level.Left isShrinkable>
              <Level.Item>
                <Text hasEllipsis size="large">
                  {
                    order.state === 'confirm'
                      ? 'Total'
                      : 'Estimated Total'
                  }
                </Text>
              </Level.Item>
            </Level.Left>
            <Level.Right>
              <Level.Item>
                <Text hasEllipsis size="large" isBold><Price value={orderTotal} /></Text>
              </Level.Item>
            </Level.Right>
          </Level>

          <Divider />

          <CheckoutAddons />

          <Box paddingVertical={1}>
            <CheckoutSidebarContent />
          </Box>
          <Divider />
          {
            step === 'confirm'
              ? (
                <Sticky position="bottom" background="alternate">
                  <Box paddingVertical={1}>
                    <Button
                      type="button"
                      color="action"
                      size="large"
                      isFullwidth
                      isLoading={isSubmitting}
                      disabled={isLoading}
                      onClick={submitOrder}
                      icon="lock"
                    >
                      Place Order
                    </Button>
                  </Box>
                </Sticky>
              )
              : null
          }
          <Box paddingTop={4} paddingBottom={1} justifyContent="center">
            <Button
              type="button"
              color="light"
              onClick={() => {
                if (!window.CustomerSupport) {
                  return
                }

                window.CustomerSupport.openChat()
                window.CustomerSupport.hideChatBubble()
              }}
            >
              Have Questions? Contact Us
            </Button>
          </Box>
        </Box>
      </Sticky>
    </Box>
  )
}

OrderSummary.propTypes = {
  order: PropTypes.shape({
    number: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
    ]),
  }), // private
  step: PropTypes.string, // private
  submitOrder: PropTypes.func, // private
  isLoading: PropTypes.bool, // private
  isSubmitting: PropTypes.bool, // private
}

OrderSummary.defaultProps = {
  order: {},
  submitOrder: () => {},
  isLoading: false,
  isSubmitting: false,
}

export default withCheckout(OrderSummary)
